import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { get, BASE_URL } from "../api/config"; // ✅ import BASE_URL

const ProjectCarousel = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [loading, setLoading] = useState(true);

  const visibleProjects = 4;

  // ✅ Fetch both ongoing and completed projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const [ongoingRes, completedRes] = await Promise.all([
          get("public/projects?status_id=2"),
          get("public/projects?status_id=3"),
        ]);

        const ongoing = Array.isArray(ongoingRes.data) ? ongoingRes.data : [];
        const completed = Array.isArray(completedRes.data)
          ? completedRes.data
          : [];

        const mixed = [...ongoing, ...completed];
        setProjects(mixed);
      } catch (err) {
        console.error("Failed to fetch projects:", err);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // ✅ Auto-slide logic
  useEffect(() => {
    if (!isAutoPlaying || projects.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % projects.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, projects.length]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % projects.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + projects.length) % projects.length);
    setIsAutoPlaying(false);
  };

  const getVisibleProjects = () => {
    const visible = [];
    for (let i = 0; i < visibleProjects; i++) {
      visible.push(projects[(currentIndex + i) % projects.length]);
    }
    return visible;
  };

  // Updated function to get project images from API data
  const getProjectImage = (project) => {
    // Check if project has timeline images
    if (project.timelines && project.timelines.length > 0) {
      const timeline = project.timelines[0];
      if (timeline.images && timeline.images.length > 0) {
        // Get image path from API response
        const imagePath = timeline.images[0].image_path;
        // Construct full URL by removing '/api' from BASE_URL and adding image path
        const baseUrl = BASE_URL.replace("/api/", "/");
        return baseUrl + imagePath;
      }
    }

    // Check if project has direct images
    if (project.images && project.images.length > 0) {
      const imagePath = project.images[0].image_path;
      const baseUrl = BASE_URL.replace("/api/", "/");
      return baseUrl + imagePath;
    }

    // Fallback images based on project name
    const name = project.project_name?.toLowerCase() || "";
    if (name.includes("drain")) {
      return "https://images.unsplash.com/photo-1581094794329-c8112c4e5190?w=600&h=600&fit=crop";
    } else if (name.includes("road")) {
      return "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=600&h=600&fit=crop";
    } else if (
      name.includes("khel") ||
      name.includes("stadium") ||
      name.includes("arena")
    ) {
      return "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=600&h=600&fit=crop";
    } else {
      return "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&h=600&fit=crop";
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    const num = parseFloat(amount);
    if (num >= 10000000) {
      return `₹${(num / 10000000).toFixed(2)} Cr`;
    } else if (num >= 100000) {
      return `₹${(num / 100000).toFixed(2)} L`;
    } else {
      return `₹${num.toLocaleString("en-IN")}`;
    }
  };

  // Get status badge color
  const getStatusBadge = (statusId, progress) => {
    switch (statusId) {
      case 2: // Ongoing
        return {
          text: `${progress}% Progress`,
          bgColor: "bg-yellow-500",
          textColor: "text-yellow-800",
        };
      case 3: // Completed
        return {
          text: "Completed",
          bgColor: "bg-green-500",
          textColor: "text-green-800",
        };
      default:
        return {
          text: "Planned",
          bgColor: "bg-blue-500",
          textColor: "text-blue-800",
        };
    }
  };

  if (loading) {
    return (
      <div className="w-full py-24 text-center text-gray-600">
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <span className="ml-4 text-lg">Loading projects...</span>
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="w-full py-24 text-center text-gray-600">
        <div className="max-w-md mx-auto">
          <div className="text-6xl mb-4">🏗️</div>
          <h3 className="text-xl font-semibold mb-2">No Projects Available</h3>
          <p className="text-gray-500">
            Projects will appear here once they are added to the system.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gradient-to-br from-gray-50 to-gray-100 py-20 px-4 overflow-hidden">
      {/* Title */}
      <div className="max-w-7xl mx-auto mb-16 text-center">
        <h2 className="text-5xl font-bold text-gray-900 mb-2">
          Ongoing & Completed Projects
        </h2>
        <div className="w-24 h-1 bg-gradient-to-r from-green-500 to-blue-500 mx-auto"></div>
        <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
          Track the progress of development projects in your constituency
        </p>
      </div>

      {/* Carousel Container */}
      <div className="relative max-w-7xl mx-auto">
        {/* Navigation Buttons */}
        <button
          onClick={prevSlide}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white shadow-2xl rounded-full p-4 transition-all duration-300 hover:scale-110 group"
          aria-label="Previous"
        >
          <svg
            className="w-8 h-8 text-gray-800 group-hover:text-green-600 transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <button
          onClick={nextSlide}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white shadow-2xl rounded-full p-4 transition-all duration-300 hover:scale-110 group"
          aria-label="Next"
        >
          <svg
            className="w-8 h-8 text-gray-800 group-hover:text-green-600 transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 px-16">
          {getVisibleProjects().map((project, index) => {
            const statusBadge = getStatusBadge(
              project.status_id,
              project.progress,
            );
            const projectImage = getProjectImage(project);

            return (
              <div
                key={`${project.project_id}-${index}`}
                className="group relative animate-fadeInScale bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                style={{
                  animationDelay: `${index * 0.1}s`,
                }}
              >
                {/* Image Container */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={projectImage}
                    alt={project.project_name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                    onError={(e) => {
                      // Fallback if image fails to load
                      const name = project.project_name?.toLowerCase() || "";
                      if (name.includes("drain")) {
                        e.target.src =
                          "https://images.unsplash.com/photo-1581094794329-c8112c4e5190?w=600&h=400&fit=crop";
                      } else if (name.includes("road")) {
                        e.target.src =
                          "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=600&h=400&fit=crop";
                      } else if (
                        name.includes("khel") ||
                        name.includes("stadium") ||
                        name.includes("arena")
                      ) {
                        e.target.src =
                          "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=600&h=400&fit=crop";
                      } else {
                        e.target.src =
                          "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&h=400&fit=crop";
                      }
                    }}
                  />

                  {/* Status Badge */}
                  <div className="absolute top-4 right-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${statusBadge.bgColor} ${statusBadge.textColor} bg-opacity-90`}
                    >
                      {statusBadge.text}
                    </span>
                  </div>

                  {/* Progress Bar for Ongoing Projects */}
                  {project.status_id === 2 && project.progress > 0 && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gray-200 h-2">
                      <div
                        className="bg-green-500 h-full transition-all duration-500"
                        style={{ width: `${project.progress}%` }}
                      ></div>
                    </div>
                  )}

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-1">
                    {project.project_name}
                  </h3>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {project.description || "No description available"}
                  </p>

                  {/* Project Details */}
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span className="text-gray-700">
                        {project.location || "Location not specified"}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-gray-700 font-medium">
                        {formatCurrency(project.estimated_cost)}
                      </span>
                    </div>

                    {project.start_date && (
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <span className="text-gray-700">
                          Started: {formatDate(project.start_date)}
                        </span>
                      </div>
                    )}

                    {/* MP Name */}
                    {project.politician && project.politician.full_name && (
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                        <span className="text-gray-700">
                          MP: {project.politician.full_name}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* View Details Button */}
                  <button 
                    onClick={() => navigate(`/projects/${project.project_id}`)}
                    className="mt-6 w-full bg-gradient-to-r from-green-500 to-blue-500 text-white px-4 py-2 rounded-lg font-semibold hover:from-green-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-[1.02] active:scale-95"
                  >
                    View Project Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Dots Indicator */}
        {projects.length > 1 && (
          <div className="flex justify-center gap-3 mt-12">
            {projects.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentIndex(index);
                  setIsAutoPlaying(false);
                }}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex
                    ? "w-12 h-3 bg-gradient-to-r from-green-500 to-blue-500"
                    : "w-3 h-3 bg-gray-300 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>
        )}

        {/* Project Count */}
        <div className="text-center mt-8 text-gray-600">
          Showing {Math.min(visibleProjects, projects.length)} of{" "}
          {projects.length} projects
        </div>

        {/* Auto-play Toggle */}
        <div className="text-center mt-4">
          <button
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            {isAutoPlaying ? "⏸️ Pause Auto-play" : "▶️ Resume Auto-play"}
          </button>
        </div>
      </div>

      {/* Animation Styles */}
      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeInScale {
          animation: fadeInScale 0.6s ease-out forwards;
        }
        .shadow-3xl {
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35);
        }
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default ProjectCarousel;
